/**
 * EditIdeaModal Component
 * 
 * Modal for editing an existing idea.
 * Allows changing title, description, category, priority, and status.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Modal,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, icon } from '../../../shared/tokens/mobile';
import {
  IdeaWithDetails,
  IdeaCategory,
  IdeaPriority,
  IdeaStatus,
  UpdateIdeaRequest,
} from '../../../shared/types/idea';
import { CATEGORIES, PRIORITIES, STATUSES, CATEGORY_CONFIG, PRIORITY_CONFIG, STATUS_CONFIG } from './ideaConfig';
import { styles } from './styles/editIdeaModalStyles';

// Default configs for safety
const DEFAULT_CATEGORY = { key: 'general' as IdeaCategory, label: 'General', icon: 'bulb-outline', color: '#6B6B6B' };
const DEFAULT_PRIORITY = { key: 'medium' as IdeaPriority, label: 'Medium', color: '#4A6B82' };
const DEFAULT_STATUS = { key: 'open' as IdeaStatus, label: 'Open', icon: 'radio-button-off-outline', color: '#4A6B82' };

interface EditIdeaModalProps {
  visible: boolean;
  idea: IdeaWithDetails;
  onClose: () => void;
  onSave: (data: UpdateIdeaRequest) => Promise<boolean>;
  saving?: boolean;
}

export function EditIdeaModal({ visible, idea, onClose, onSave, saving }: EditIdeaModalProps) {
  const [title, setTitle] = useState(idea.title);
  const [description, setDescription] = useState(idea.description || '');
  const [category, setCategory] = useState<IdeaCategory>(idea.category);
  const [priority, setPriority] = useState<IdeaPriority>(idea.priority);
  const [status, setStatus] = useState<IdeaStatus>(idea.status);
  const [resolutionNotes, setResolutionNotes] = useState(idea.resolution_notes || '');
  const [error, setError] = useState<string | null>(null);

  // Reset form when idea changes
  useEffect(() => {
    setTitle(idea.title);
    setDescription(idea.description || '');
    setCategory(idea.category);
    setPriority(idea.priority);
    setStatus(idea.status);
    setResolutionNotes(idea.resolution_notes || '');
    setError(null);
  }, [idea]);

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setError('Please enter a title');
      return;
    }

    setError(null);
    
    const data: UpdateIdeaRequest = {};
    
    if (title.trim() !== idea.title) data.title = title.trim();
    if (description.trim() !== (idea.description || '')) data.description = description.trim();
    if (category !== idea.category) data.category = category;
    if (priority !== idea.priority) data.priority = priority;
    if (status !== idea.status) {
      data.status = status;
      // Include resolution notes if resolving
      if (['approved', 'rejected', 'implemented'].includes(status) && resolutionNotes.trim()) {
        data.resolution_notes = resolutionNotes.trim();
      }
    }

    if (Object.keys(data).length === 0) {
      onClose();
      return;
    }

    const success = await onSave(data);
    if (success) {
      onClose();
    }
  }, [title, description, category, priority, status, resolutionNotes, idea, onSave, onClose]);

  const isResolving = ['approved', 'rejected', 'implemented'].includes(status) && 
                      !['approved', 'rejected', 'implemented'].includes(idea.status);

  return (
    <Modal visible={visible} animationType="slide" presentationStyle="pageSheet" onRequestClose={onClose}>
      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={styles.container}>
        <View style={styles.header}>
          <Pressable onPress={onClose} style={styles.closeButton}>
            <Text style={styles.cancelText}>Cancel</Text>
          </Pressable>
          <Text style={styles.headerTitle}>Edit Idea</Text>
          <Pressable 
            onPress={handleSave} 
            disabled={saving || !title.trim()}
            style={[styles.saveButton, (saving || !title.trim()) && styles.saveButtonDisabled]}
          >
            {saving ? <ActivityIndicator size="small" color={color.surface} /> : (
              <Text style={styles.saveButtonText}>Save</Text>
            )}
          </Pressable>
        </View>

        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          {error && (
            <View style={styles.errorBanner}>
              <Ionicons name="alert-circle" size={icon.sm} color={color.error} />
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <View style={styles.field}>
            <Text style={styles.label}>Title *</Text>
            <TextInput style={styles.titleInput} value={title} onChangeText={setTitle} maxLength={255} />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={styles.descriptionInput}
              value={description}
              onChangeText={setDescription}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Category</Text>
            <View style={styles.optionsGrid}>
              {CATEGORIES.map(cat => {
                const isSelected = category === cat.key;
                const config = CATEGORY_CONFIG[cat.key] || DEFAULT_CATEGORY;
                return (
                  <Pressable
                    key={cat.key}
                    onPress={() => setCategory(cat.key)}
                    style={[styles.optionButton, isSelected && { backgroundColor: config.color + '15', borderColor: config.color }]}
                  >
                    <Ionicons name={config.icon as any} size={icon.md} color={isSelected ? config.color : color.textSecondary} />
                    <Text style={[styles.optionLabel, isSelected && { color: config.color }]}>{config.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Priority</Text>
            <View style={styles.priorityRow}>
              {PRIORITIES.map(pri => {
                const isSelected = priority === pri.key;
                const config = PRIORITY_CONFIG[pri.key] || DEFAULT_PRIORITY;
                return (
                  <Pressable
                    key={pri.key}
                    onPress={() => setPriority(pri.key)}
                    style={[styles.priorityButton, isSelected && { backgroundColor: config.color + '15', borderColor: config.color }]}
                  >
                    <View style={[styles.priorityDot, { backgroundColor: config.color }]} />
                    <Text style={[styles.priorityLabel, isSelected && { color: config.color, fontWeight: '600' }]}>{config.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <View style={styles.field}>
            <Text style={styles.label}>Status</Text>
            <View style={styles.statusGrid}>
              {STATUSES.map(stat => {
                const isSelected = status === stat.key;
                const config = STATUS_CONFIG[stat.key] || DEFAULT_STATUS;
                return (
                  <Pressable
                    key={stat.key}
                    onPress={() => setStatus(stat.key)}
                    style={[styles.statusButton, isSelected && { backgroundColor: config.color + '15', borderColor: config.color }]}
                  >
                    <Ionicons name={config.icon as any} size={14} color={isSelected ? config.color : color.textSecondary} />
                    <Text style={[styles.statusLabel, isSelected && { color: config.color }]}>{config.label}</Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          {isResolving && (
            <View style={styles.field}>
              <Text style={styles.label}>Resolution Notes</Text>
              <TextInput
                style={styles.descriptionInput}
                placeholder="Why is this being resolved this way?"
                placeholderTextColor={color.textMuted}
                value={resolutionNotes}
                onChangeText={setResolutionNotes}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );
}
